import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { History, ChevronDown, ChevronUp, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import IntakeFormFill from "@/components/IntakeFormFill";
import { fmtDate, fmtTimeRange } from "@/lib/format";

const fmtDur = (iso) => {
  if (!iso) return "";
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  if (days < 31) return `${days} day${days === 1 ? "" : "s"}`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? "" : "s"}`;
  const y = Math.floor(days / 365), m = Math.floor((days % 365) / 30);
  return `${y} year${y > 1 ? "s" : ""}${m ? ` ${m} mo` : ""}`;
};

export const MemberHistoryPanel = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["me-history"], queryFn: () => api.get("/me/history").then((r) => r.data) });
  const { data: pend } = useQuery({ queryKey: ["me-intake-pending"], queryFn: () => api.get("/me/intake/pending").then((r) => r.data) });
  const pending = pend?.pending || [];

  if (!data) return null;
  const s = data.stats;

  return (
    <>
      {pending.map((p) => (
        <div key={p.booking_id || p.registration_id} className="eyw-card p-6 md:p-7 mt-8 border-[#D98E73]/40 bg-[#D98E73]/[0.04]" data-testid={`pending-intake-${p.booking_id || p.registration_id}`}>
          <IntakeFormFill bookingId={p.booking_id} registrationId={p.registration_id} formId={p.form_id} questions={p.questions}
            title={p.reason === "event_form" ? `${p.service_title} — before ${p.date}` : `Before your ${p.service_title}${p.enhancer_name ? ` with ${p.enhancer_name}` : ""} on ${p.date}`}
            onDone={() => { qc.invalidateQueries({ queryKey: ["me-intake-pending"] }); qc.invalidateQueries({ queryKey: ["me-history"] }); }} />
        </div>
      ))}

      <div className="eyw-card p-8 mt-8" data-testid="my-history">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><History size={20} className="text-[#144F75]" /> My wellness history</h2>
          <p className="text-sm text-[#8FA89B]" data-testid="my-membership-duration">Member for {fmtDur(data.user.member_since)}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {[["Sessions completed", s.sessions_completed], ["Upcoming sessions", s.sessions_upcoming], ["Events attended", s.events_attended], ["Active packages", s.packages_active]].map(([l, v]) => (
            <div key={l} className="rounded-2xl bg-[#F9F7F3] border border-[#E5E0D8] p-4 text-center">
              <p className="text-2xl font-serif text-[#144F75]">{v}</p>
              <p className="text-xs text-[#8FA89B] mt-0.5">{l}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="my-history-toggle" className="mt-5 text-sm text-[#144F75] font-medium flex items-center gap-1 hover:underline">
          {open ? <>Hide full history <ChevronUp size={15} /></> : <>See full history <ChevronDown size={15} /></>}
        </button>
        {open && (
          <div className="grid lg:grid-cols-2 gap-6 mt-5" data-testid="my-history-detail">
            <div>
              <p className="eyw-label mb-2">Sessions</p>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {data.sessions.map((b) => (
                  <div key={b.id} className="flex items-center justify-between border-b border-[#E5E0D8] pb-2 text-sm">
                    <span className="text-[#1A242B]">{b.service_title}<span className="text-xs text-[#8FA89B] block">{fmtDate(b.date)} {fmtTimeRange(b.start_time)}{b.enhancer_name ? ` · ${b.enhancer_name}` : ""}</span></span>
                    <span className="text-xs text-[#8FA89B] capitalize">{b.status}</span>
                  </div>
                ))}
                {data.sessions.length === 0 && <p className="text-sm text-[#8FA89B]">No sessions yet.</p>}
              </div>
            </div>
            <div>
              <p className="eyw-label mb-2">Events & classes</p>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {data.events.map((e) => (
                  <div key={e.id} className="flex items-center justify-between border-b border-[#E5E0D8] pb-2 text-sm">
                    <span className="text-[#1A242B]">{e.event_title}<span className="text-xs text-[#8FA89B] block">{fmtDate(e.date)}</span></span>
                    {e.checked_in && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">attended</span>}
                  </div>
                ))}
                {data.events.length === 0 && <p className="text-sm text-[#8FA89B]">No events yet.</p>}
              </div>
              {data.intake_responses.length > 0 && (
                <p className="text-xs text-[#8FA89B] mt-4 flex items-center gap-1.5"><ClipboardList size={13} /> {data.intake_responses.length} intake form{data.intake_responses.length > 1 ? "s" : ""} on file with your practitioners.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MemberHistoryPanel;
